import React, { useMemo, useState } from 'react';

import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import CustomButton from '@/DesignSystem/components/button/CustomButton';

const CustomTableWithLoadMore = ({
    columns,
    rows,
    title,
    defaultSortFieldId,
    onClickLoadMore,
    defaultOrder = 'desc',
    hideLoadMore = false,
    rowHeight = 30,
    tableSx = {},
}) => {
    const [order, setOrder] = useState(defaultOrder);
    const [orderBy, setOrderBy] = useState(defaultSortFieldId);

    const theme = useTheme();

    const handleRequestSort = (property, sortable) => {
        if (!sortable) {
            return;
        }

        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedRows = useMemo(() => {
        return rows?.slice()?.sort((a, b) => {
            const column = columns.find((col) => col.id === orderBy) || columns[0];
            const aValue = column.sortFunction ? column.sortFunction(a, b) : a[orderBy];
            const bValue = column.sortFunction ? column.sortFunction(b, a) : b[orderBy];
            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [rows, columns, orderBy, order]);

    if (!rows || rows.length === 0) {
        return null;
    }

    return (
        <Box sx={{ width: '100%' }}>
            {title?.length > 1 && (
                <Box
                    sx={{
                        p: 1.5,
                        backgroundColor: 'background.default',
                        borderColor: theme.palette.border.main,
                    }}
                >
                    <Typography variant="h6" color="text.primary">
                        {title}
                    </Typography>
                </Box>
            )}

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    maxWidth: '100%',
                    position: 'relative',
                    border: `1px solid ${theme.palette.border.light}`,
                    backgroundColor: 'background.default',
                    ...tableSx,
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    onClick={() => handleRequestSort(column.id, column.sortable)}
                                    sx={{
                                        cursor: 'pointer',
                                        minWidth: column.width ?? 100,
                                        width: column.width ?? 'auto',
                                        whiteSpace: 'nowrap',
                                        position: column.fixed ? 'sticky' : 'relative',
                                        left: column.fixed ? 0 : 'auto',
                                        backgroundColor: 'background.card',
                                        zIndex: column.fixed ? 2 : 1,
                                        textTransform: 'uppercase',
                                        fontWeight: 'bold',
                                        fontSize: '0.875rem',
                                        color: 'text.primary',
                                        borderBottom: `2px solid`,
                                        borderColor: theme.palette.border.light,
                                        padding: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            height: '30px',
                                            justifyContent: column.left
                                                ? 'flex-start'
                                                : column.right
                                                    ? 'flex-end'
                                                    : 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.primary"
                                            fontSize={column.fontSize ?? '14px'}
                                        >
                                            {column.name}
                                        </Typography>
                                        {orderBy === column.id && column.sortable && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: column.fontSize ?? '14px',
                                                }}
                                            >
                                                {order === 'asc' ? '▲' : '▼'}
                                            </Typography>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows?.map((row, index) => {
                            return (
                                <TableRow
                                    key={row.id || `row-${index}`}
                                    sx={{
                                        height: rowHeight,
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            sx={{
                                                minWidth: column.width ?? 100,
                                                width: column.width ?? 'auto',
                                                position: column.fixed ? 'sticky' : 'relative',
                                                left: column.fixed ? 0 : 'auto',
                                                backgroundColor: 'background.default',
                                                zIndex: column.fixed ? 1 : 0,
                                                textAlign: column.center
                                                    ? 'center'
                                                    : column.right
                                                        ? 'right'
                                                        : 'left',
                                                color: 'text.primary',
                                                borderColor: theme.palette.border.light,
                                                padding: 0,
                                            }}
                                        >
                                            {column.cell(row, index)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {!hideLoadMore && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <CustomButton variant="outlined" color="secondary" onClick={onClickLoadMore}>
                        Cargar más
                    </CustomButton>
                </Box>
            )}
        </Box>
    );
};

export default CustomTableWithLoadMore;
